import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import {
  SCOPES, TIERS, tierGranted, summarise, computeExpiry, buildTermsSnapshot, hashTerms,
} from '../../lib/licence'
import { Shell } from '../../components/Shell'
import { Button, Check, Progress, Saved, Spinner } from '../../components/ui'

const TOTAL = 4

/**
 * The pastor's side of a proposed licence, reframed at the tier a non-lawyer
 * actually decides in — "Approved for Congregational Use" / "Approved for
 * External Use" — rather than five scope checkboxes cold. Mirrors the two
 * tiers in the Secret Place Music legal brief (request for advice, 17 Aug
 * 2026): Congregational Use is the everyday in-house grant, External Use is
 * the one that leaves the building.
 *
 * Unlike the songwriter's Endorse.jsx (which deliberately saves nothing until
 * the final "propose" — nothing is agreed until then), a tier decision here
 * saves the moment it's made. A pastor reviewing this mid-service, or handing
 * it to an associate to finish, should never lose a decision they already
 * made because the tab closed. The licence stays status='proposed' the whole
 * time — nothing is final, and nothing is granted, until the last tile.
 *
 * Eventually this should only be reachable once a song has passed a
 * reviewer's first look. For now it's reachable the moment a songwriter
 * proposes terms at all, same as Endorse.jsx always has been — there's no
 * extra gate to remove later, just one to add.
 */
export default function EndorsementReview() {
  const { id, step } = useParams()
  const nav = useNavigate()
  const { church } = useSession()

  const [row, setRow] = useState(null)
  const [ready, setReady] = useState(false)
  const [err, setErr] = useState(null)
  const [saveState, setSaveState] = useState('idle')

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('song_licences')
      .select('*, songs(title), songwriter:users!song_licences_songwriter_id_fkey(first_name, last_name, email)')
      .eq('id', id).maybeSingle()
    if (error) setErr(readableError(error))
    setRow(data ?? null)
    setReady(true)
  }, [id])

  useEffect(() => { load() }, [load])

  if (!ready) return <Shell hidePlayer><Spinner /></Shell>

  if (!row || !church || row.church_id !== church.id) {
    return (
      <Shell hidePlayer back="/admin/endorsements" title="Endorsements">
        <h2 className="q">We can't find that licence.</h2>
      </Shell>
    )
  }

  const songTitle = row.songs?.title || 'Untitled'
  const writerName = [row.songwriter?.first_name, row.songwriter?.last_name].filter(Boolean).join(' ')
    || row.songwriter?.email || 'The songwriter'

  // Checked after the done screen, not before it — confirm() moves status to
  // 'active' without updating this component's local row, so a refresh on
  // the done URL would otherwise re-fetch the now-active row and land here
  // instead of the confirmation it just earned.
  if (step === 'done') return <Done row={row} songTitle={songTitle} />

  if (row.status !== 'proposed') {
    return (
      <Shell hidePlayer back="/admin/endorsements" title="Endorsements">
        <h2 className="q">This one's already settled.</h2>
        <p className="sub">Its decision was made — see it on the Endorsements list.</p>
        <button className="btn btn-primary" onClick={() => nav('/admin/endorsements')}>
          Back to Endorsements
        </button>
      </Shell>
    )
  }

  const go = (n) => nav(`/admin/endorsements/${id}/review/${n}`)

  /** One tier decision, saved the moment it's made. */
  const setTier = async (tier, granted) => {
    setErr(null)
    setSaveState('saving')
    const patch = {}
    tier.scopeIds.forEach((sid) => { patch[SCOPES.find((s) => s.id === sid).column] = granted })
    const { error } = await supabase.from('song_licences').update(patch).eq('id', id)
    if (error) { setSaveState('error'); return setErr(readableError(error)) }
    setRow((r) => ({ ...r, ...patch }))
    setSaveState('saved')
  }

  const common = { row, songTitle, writerName, church, go, setTier, saveState, err, setErr }

  const n = Math.min(Math.max(parseInt(step, 10) || 1, 1), TOTAL)
  const Step = [R1, R2, R3, R4][n - 1]
  return <Step {...common} n={n} />
}

/** The tile shell every review step shares — title, blurb, the tier's chip,
    and the "weeds" detail underneath for anyone who wants it. */
function TierTile({ tier, granted, onToggle, saving }) {
  return (
    <div className="tile" style={{ marginTop: 6 }}>
      <button
        type="button"
        className={`chip chip-wide${granted ? ' on' : ''}`}
        style={{ width: '100%' }}
        disabled={saving}
        onClick={() => onToggle(!granted)}
      >
        <span style={{ flex: 1 }}>
          {tier.label}
          <small>{tier.blurb}</small>
        </span>
        {granted ? <Check /> : '○'}
      </button>

      {tier.excludes && (
        <p className="small" style={{ marginTop: 10, marginBottom: 0 }}>{tier.excludes}</p>
      )}

      <details style={{ marginTop: 10 }}>
        <summary className="small" style={{ cursor: 'pointer', fontWeight: 600 }}>
          What exactly this covers
        </summary>
        <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
          {tier.scopeIds.map((sid) => {
            const s = SCOPES.find((x) => x.id === sid)
            return (
              <li key={sid} className="small" style={{ margin: '4px 0' }}>
                <b>{s.label}</b> — {s.blurb}
              </li>
            )
          })}
        </ul>
      </details>
    </div>
  )
}

/* ------------------------------------------------------------------- R1 */
function R1({ songTitle, writerName, church, go, n }) {
  return (
    <Shell hidePlayer back="/admin/endorsements" title="Endorsements">
      <Progress step={n} total={TOTAL} />
      <div className="eyebrow">Licence review</div>
      <h2 className="q">{writerName} wants {church.name} to be able to use “{songTitle}”.</h2>
      <p className="sub">
        The copyright stays with {writerName} throughout — you're not being asked to sign
        anything away. This is a licence: named permission to use one specific song, on
        terms you set here, that can be narrowed or ended later.
      </p>
      <div className="trust">
        <p className="small" style={{ margin: 0 }}>
          The next two screens ask the two questions that actually matter: can your church
          use this in the room, and can it leave the room. Everything else is detail
          underneath, for whenever you want it.
        </p>
      </div>
      <div className="hr" />
      <button className="btn btn-primary" onClick={() => go(2)}>Next — Congregational Use</button>
    </Shell>
  )
}

/* ------------------------------------------------------------------- R2 */
function R2({ row, go, n, setTier, saveState }) {
  const tier = TIERS[0]
  const granted = tierGranted(row, tier)

  return (
    <Shell hidePlayer back={`/admin/endorsements/${row.id}/review/1`} title="Back">
      <Progress step={n} total={TOTAL} />
      <div className="eyebrow">Licence review</div>
      <h2 className="q">Can your church use it, in the room?</h2>
      <p className="sub">The everyday, in-house use — nothing here leaves your congregation.</p>

      <TierTile tier={tier} granted={granted} saving={saveState === 'saving'}
                onToggle={(v) => setTier(tier, v)} />

      <div style={{ marginTop: 10 }}><Saved state={saveState} /></div>

      <div className="hr" />
      <button className="btn btn-primary" onClick={() => go(3)}>Next — External Use</button>
    </Shell>
  )
}

/* ------------------------------------------------------------------- R3 */
function R3({ row, go, n, setTier, saveState }) {
  const tier = TIERS[1]
  const granted = tierGranted(row, tier)

  return (
    <Shell hidePlayer back={`/admin/endorsements/${row.id}/review/2`} title="Back">
      <Progress step={n} total={TOTAL} />
      <div className="eyebrow">Licence review</div>
      <h2 className="q">Can it leave the room?</h2>
      <p className="sub">
        Recording it and putting it online — separate from playing it live, and off by
        default. Saying no here doesn't touch what you approved for Congregational Use.
      </p>

      <TierTile tier={tier} granted={granted} saving={saveState === 'saving'}
                onToggle={(v) => setTier(tier, v)} />

      <div style={{ marginTop: 10 }}><Saved state={saveState} /></div>

      <div className="hr" />
      <button className="btn btn-primary" onClick={() => go(4)}>Next — review &amp; decide</button>
    </Shell>
  )
}

/* ------------------------------------------------------------------- R4 */
function R4({ row, songTitle, writerName, go, err, setErr }) {
  const { user } = useSession()
  const [busy, setBusy] = useState(false)
  const nav = useNavigate()
  const lines = summarise(row)
  const congregational = tierGranted(row, TIERS[0])
  const external = tierGranted(row, TIERS[1])

  const confirm = async () => {
    setBusy(true); setErr(null)
    const startsAt = new Date()
    const expiresAt = computeExpiry(startsAt, row.duration_months)
    const snapshot = buildTermsSnapshot(row, user?.id, startsAt, expiresAt)
    const hash = await hashTerms(snapshot)
    const { error } = await supabase.from('song_licences').update({
      status: 'active',
      accepted_at: startsAt.toISOString(),
      accepted_by: user?.id,
      starts_at: startsAt.toISOString(),
      expires_at: expiresAt ? expiresAt.toISOString() : null,
      terms_snapshot: snapshot,
      terms_hash: hash,
    }).eq('id', row.id)
    setBusy(false)
    if (error) return setErr(readableError(error))
    go('done')
  }

  const decline = async () => {
    if (!window.confirm(
      `Decline this licence? ${writerName} would need to propose it again — this can't be undone.`
    )) return
    setBusy(true); setErr(null)
    const { error } = await supabase.from('song_licences')
      .update({ status: 'declined' }).eq('id', row.id)
    setBusy(false)
    if (error) return setErr(readableError(error))
    nav('/admin/endorsements')
  }

  return (
    <Shell hidePlayer back={`/admin/endorsements/${row.id}/review/3`} title="Back">
      <Progress step={4} total={TOTAL} />
      <div className="eyebrow">Licence review</div>
      <h2 className="q">Here's the decision on “{songTitle}”.</h2>

      <div className="stack" style={{ marginTop: 6 }}>
        <div className="tile" style={{ margin: 0 }}>
          <p style={{ margin: 0, fontWeight: 600 }}>
            {congregational ? '✓ ' : '— '}Approved for Congregational Use
          </p>
        </div>
        <div className="tile" style={{ margin: 0 }}>
          <p style={{ margin: 0, fontWeight: 600 }}>
            {external ? '✓ ' : '— '}Approved for External Use
          </p>
        </div>
      </div>

      <p className="small" style={{ marginTop: 16, fontWeight: 600 }}>The rest of {writerName}'s proposed terms</p>
      <div className="stack" style={{ marginTop: 6 }}>
        {lines.map((line, i) => (
          <div className="tile" key={i} style={{ margin: 0 }}>
            <p style={{ margin: 0 }}>{line}</p>
          </div>
        ))}
      </div>

      {!congregational && !external && (
        <p className="small" style={{ marginTop: 12 }}>
          Nothing is approved yet — confirming now sends {writerName} a licence with no uses
          granted. You can also decline outright instead.
        </p>
      )}

      {err && <p className="err">{err}</p>}
      <div className="hr" />
      <Button busy={busy} onClick={confirm}>Confirm this decision</Button>
      <button className="btn btn-quiet" style={{ marginTop: 10 }} disabled={busy} onClick={decline}>
        Decline the licence outright
      </button>
    </Shell>
  )
}

/* ---------------------------------------------------------------- done */
function Done({ row, songTitle }) {
  const nav = useNavigate()
  const congregational = tierGranted(row, TIERS[0])
  const external = tierGranted(row, TIERS[1])
  return (
    <Shell hidePlayer back="/admin/endorsements" title="Endorsements">
      <div className="done-ic"><Check size={26} w={2.4} /></div>
      <h2 className="q center">Decision recorded.</h2>
      <p className="sub center">
        “{songTitle}” is now licensed to your church for {congregational && external ? 'Congregational and External Use' : congregational ? 'Congregational Use only' : external ? 'External Use only' : 'no uses'}.
        The songwriter has been sent this decision.
      </p>
      <div className="hr" />
      <button className="btn btn-primary" onClick={() => nav('/admin/endorsements')}>
        Back to Endorsements
      </button>
    </Shell>
  )
}
